import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  queryRecordsPage,
  adminClearUserRecords,
  adminDeleteUserRecord,
  type AdminRecordType,
} from '@/client/admin.client';

const RECORD_TYPES: { type: AdminRecordType; label: string; idKey: 'sessionId' | 'timestamp' }[] = [
  { type: 'STAGE_HISTORY', label: 'Stage History', idKey: 'sessionId' },
  { type: 'LEAGUE_HISTORY', label: 'League History', idKey: 'sessionId' },
  { type: 'VALHALLA_HISTORY', label: 'Valhalla History', idKey: 'sessionId' },
  { type: 'DEGEN_HISTORY', label: 'Degen History', idKey: 'sessionId' },
  { type: 'SUSPICIOUS_LOG', label: 'Suspicious Logs', idKey: 'timestamp' },
];

export function UserRecordsPanel({ account }: { account: string }) {
  const [openType, setOpenType] = useState<AdminRecordType | null>(null);
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(type: AdminRecordType, reset = true) {
    setBusy(true);
    try {
      const res = await queryRecordsPage({
        type,
        account,
        cursor: reset ? null : cursor,
        limit: 50,
      });
      setRecords((prev) => (reset ? res.records : [...prev, ...res.records]));
      setTotal(res.total);
      setHasMore(res.hasMore);
      setCursor(res.nextCursor);
      setOpenType(type);
    } catch (e) {
      window.alert(`조회 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function clearAll(type: AdminRecordType, label: string) {
    if (!window.confirm(`${account} 의 ${label} 를 전체 삭제할까요?`)) return;
    setBusy(true);
    try {
      const res = await adminClearUserRecords({ type, account });
      window.alert(`${res.cleared}건 삭제됨`);
      if (openType === type) {
        setRecords([]);
        setTotal(0);
        setHasMore(false);
        setCursor(null);
        setOpenType(null);
      }
    } catch (e) {
      window.alert(`삭제 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function deleteOne(
    type: AdminRecordType,
    idKey: 'sessionId' | 'timestamp',
    row: Record<string, unknown>
  ) {
    const idValue = row[idKey];
    if (idValue === undefined || idValue === null) {
      window.alert(`이 항목에 ${idKey} 가 없어 삭제할 수 없습니다.`);
      return;
    }
    const matcher =
      idKey === 'timestamp' ? { timestamp: idValue as number } : { sessionId: idValue as string };
    if (!window.confirm(`이 항목을 삭제할까요? (${idKey}=${String(idValue)})`)) return;
    setBusy(true);
    try {
      await adminDeleteUserRecord({ type, account, matcher });
      setRecords((prev) => prev.filter((r) => r[idKey] !== idValue));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      window.alert(`삭제 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">History / Suspicious Logs</h3>
      {RECORD_TYPES.map(({ type, label, idKey }) => (
        <div key={type} className="border rounded p-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{label}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void load(type)}>
                View
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => void clearAll(type, label)}
              >
                Clear all
              </Button>
            </div>
          </div>
          {openType === type && (
            <>
              <div className="mt-2 max-h-80 overflow-auto text-xs">
                <div className="text-muted-foreground mb-1">total: {total}</div>
                {records.map((row, i) => (
                  <div
                    key={String(row[idKey] ?? i)}
                    className="flex items-start justify-between gap-2 border-b py-1"
                  >
                    <pre className="whitespace-pre-wrap break-all flex-1">
                      {JSON.stringify(row)}
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void deleteOne(type, idKey, row)}
                    >
                      삭제
                    </Button>
                  </div>
                ))}
              </div>
              {hasMore && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void load(type, false)}
                >
                  더 보기 ({records.length}/{total})
                </Button>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default UserRecordsPanel;
